import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { VulnerabilityReport } from "@/components/VulnerabilityReport";
import { EPIC } from "@/components/roleplay/PersonaGrid";
import { RadarChart, PolarGrid, PolarAngleAxis, Radar, ResponsiveContainer } from "recharts";
import {
  Loader2, ArrowLeft, Sparkles, Trophy, Target, Lightbulb, ListChecks, RefreshCw, Hand,
  Share2, Copy, ExternalLink, ImageDown, Route, Shuffle, Eye, EyeOff, Award
} from "lucide-react";

const SCORE_LABELS = {
  opening: "Opening", confidence: "Confidence", rapport: "Rapport",
  questions: "Questions", listening: "Listening", objection_handling: "Objections",
  closing: "Closing", adaptability: "Adaptability", sales_psychology: "Psychology",
};

const COACH_AVATAR_URL = "https://static.prod-images.emergentagent.com/jobs/1a9e1561-ca18-4dbb-8f52-13e836a03554/images/063325646653cf8a151ee440b3e604a7efe09ddef4648003466f156b1d0b378f.jpeg";

function ScoreRing({ value, size = 150, stroke = 12, color }) {
  const r = (size - stroke) / 2, c = 2 * Math.PI * r;
  return (
    <div className="relative shrink-0" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        <circle cx={size / 2} cy={size / 2} r={r} stroke="#E2E8F0" strokeWidth={stroke} fill="none" />
        <motion.circle cx={size / 2} cy={size / 2} r={r} stroke={color} strokeWidth={stroke} fill="none" strokeLinecap="round"
          strokeDasharray={c} initial={{ strokeDashoffset: c }} animate={{ strokeDashoffset: c - (Math.min(100, Math.max(0, value)) / 100) * c }} transition={{ duration: 1.2, ease: "easeOut", delay: 0.2 }} />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div className="text-5xl font-extrabold tracking-tight font-display text-slate-900 leading-none" data-testid="overall-rating">{value}</div>
        <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mt-1">out of 100</div>
      </div>
    </div>
  );
}

const fadeUp = { hidden: { opacity: 0, y: 16 }, show: { opacity: 1, y: 0, transition: { duration: 0.45, ease: [0.25, 1, 0.5, 1] } } };
const Section = ({ children, className = "", testid }) => <motion.section variants={fadeUp} className={`${EPIC} ${className}`} data-testid={testid}>{children}</motion.section>;
const SectionTitle = ({ icon: Icon, tone, children }) => <h3 className="text-base md:text-lg font-bold tracking-tight text-slate-900 flex items-center gap-2"><Icon className={`w-5 h-5 ${tone}`} /> {children}</h3>;

export function Scorecard({ analysis, persona, onRetry, onBack, xpGained, sessionId, assessment }) {
  const navigate = useNavigate();
  const radarData = Object.keys(SCORE_LABELS).map((k) => ({ metric: SCORE_LABELS[k], value: analysis.scores?.[k] ?? 0 }));
  const rating = analysis.overall_rating ?? 0;
  const tone = rating >= 80 ? { hex: "#10B981", text: "text-emerald-700", chip: "bg-emerald-100/80 text-emerald-800 border-emerald-200/80", word: "Elite performance" }
    : rating >= 60 ? { hex: "#F59E0B", text: "text-amber-700", chip: "bg-amber-100/80 text-amber-800 border-amber-200/80", word: "Solid — sharpen the edges" }
    : { hex: "#EF4444", text: "text-red-700", chip: "bg-red-100/80 text-red-800 border-red-200/80", word: "Room to grow" };
  const scores = Object.entries(SCORE_LABELS).map(([k, label]) => ({ k, label, v: analysis.scores?.[k] ?? 0 })).sort((a, b) => b.v - a.v);
  const best = scores[0], worst = scores[scores.length - 1];

  return (
    <motion.div initial="hidden" animate="show" variants={{ hidden: { opacity: 0 }, show: { opacity: 1, transition: { staggerChildren: 0.07 } } }} className="space-y-6" data-testid="analysis-view">
      <Section className="relative overflow-hidden p-6 sm:p-8" testid="scorecard-hero">
        <div className="absolute -top-24 -right-20 w-72 h-72 rounded-full blur-3xl pointer-events-none" style={{ background: `${tone.hex}22` }} />
        <div className="absolute -bottom-28 left-1/4 w-80 h-80 rounded-full bg-cyan-400/10 blur-3xl pointer-events-none" />
        <div className="relative flex items-center gap-3">
          <Button variant="ghost" size="icon" onClick={onBack} className="text-slate-500 hover:text-slate-900 -ml-2" data-testid="analysis-back-btn"><ArrowLeft className="w-5 h-5" /></Button>
          <p className="text-xs font-bold uppercase tracking-widest text-emerald-700">Scorecard · {persona.title}</p>
        </div>
        <div className="relative mt-3 grid md:grid-cols-[auto_1fr] gap-6 items-center">
          <div className="flex items-center gap-5">
            <ScoreRing value={rating} color={tone.hex} />
            <div className="md:hidden">
              <div className={`text-lg font-extrabold tracking-tight ${tone.text}`}>{tone.word}</div>
              <span className={`mt-2 inline-flex px-3 py-1 rounded-full text-xs font-bold border ${analysis.won ? "bg-emerald-100/80 text-emerald-800 border-emerald-200/80" : "bg-slate-100 text-slate-700 border-slate-200"}`}>{analysis.won ? "Deal won" : "Deal not closed"}</span>
            </div>
          </div>
          <div className="min-w-0">
            <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight text-slate-900 leading-tight"><span className={tone.text}>{tone.word}</span> against {persona.name}.</h1>
            <div className="mt-3 flex flex-wrap items-center gap-2">
              <span className={`hidden md:inline-flex px-3 py-1 rounded-full text-xs font-bold border ${analysis.won ? "bg-emerald-100/80 text-emerald-800 border-emerald-200/80" : "bg-slate-100 text-slate-700 border-slate-200"}`} data-testid="deal-chip">{analysis.won ? "Deal won" : "Deal not closed"}</span>
              {xpGained != null && <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold bg-amber-100/80 text-amber-800 border border-amber-200/80" data-testid="xp-gained"><Sparkles className="w-3.5 h-3.5" /> +{xpGained} XP</span>}
              {best && <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-white border border-slate-200 text-slate-700"><Trophy className="w-3.5 h-3.5 text-emerald-600" /> Best: {best.label} {best.v}</span>}
              {worst && <span className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold bg-white border border-slate-200 text-slate-700"><Target className="w-3.5 h-3.5 text-red-500" /> Work on: {worst.label} {worst.v}</span>}
            </div>
            <div className="mt-5 flex flex-wrap gap-3">
              <Button onClick={onRetry} className="h-11 px-5 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold shadow-[0_4px_14px_rgba(16,185,129,0.35)]" data-testid="analysis-retry-btn"><RefreshCw className="w-4 h-4 mr-2" /> Run it again</Button>
              <Button onClick={onBack} variant="outline" className="h-11 px-5 rounded-xl border-slate-300 bg-white text-slate-900 hover:bg-slate-50 font-semibold" data-testid="analysis-newcustomer-btn">New customer</Button>
            </div>
          </div>
        </div>
      </Section>

      <div className="grid lg:grid-cols-5 gap-6">
        <Section className="p-5 lg:col-span-2" testid="skill-radar-card">
          <SectionTitle icon={Target} tone="text-emerald-600">Skill map</SectionTitle>
          <ResponsiveContainer width="100%" height={250}>
            <RadarChart data={radarData} outerRadius="72%">
              <PolarGrid stroke="#E2E8F0" />
              <PolarAngleAxis dataKey="metric" tick={{ fill: "#64748B", fontSize: 11 }} />
              <Radar dataKey="value" stroke="#10B981" fill="#10B981" fillOpacity={0.3} strokeWidth={2} />
            </RadarChart>
          </ResponsiveContainer>
        </Section>
        <Section className="p-5 lg:col-span-3" testid="skill-bars-card">
          <SectionTitle icon={ListChecks} tone="text-cyan-600">Skill breakdown</SectionTitle>
          <div className="mt-4 grid sm:grid-cols-2 gap-x-6 gap-y-3">
            {scores.map((sc, i) => (
              <div key={sc.k} data-testid={`score-${sc.k}`}>
                <div className="flex items-center justify-between text-xs"><span className="font-medium text-slate-700">{sc.label}</span><span className="font-mono font-bold text-slate-900">{sc.v}</span></div>
                <div className="h-2 mt-1 rounded-full bg-slate-100 overflow-hidden">
                  <motion.div initial={{ width: 0 }} animate={{ width: `${sc.v}%` }} transition={{ delay: 0.3 + i * 0.05, duration: 0.7 }} className={`h-full rounded-full ${sc.v >= 70 ? "bg-emerald-500" : sc.v >= 45 ? "bg-cyan-500" : "bg-amber-500"}`} />
                </div>
              </div>
            ))}
          </div>
        </Section>
      </div>

      <Section className="p-6 bg-gradient-to-br from-white via-white to-emerald-50/60" testid="verdict-card">
        <div className="flex items-start gap-4">
          <img src={COACH_AVATAR_URL} alt="Linden" className="w-12 h-12 rounded-xl object-cover object-top ring-2 ring-white shadow shrink-0" />
          <div>
            <div className="text-xs font-bold uppercase tracking-widest text-emerald-700">Linden's verdict</div>
            <p className="text-slate-800 mt-1.5 leading-relaxed">{analysis.summary}</p>
          </div>
        </div>
      </Section>

      {analysis.interrupt_recovery != null && (
        <Section className="p-5 border-red-200" testid="interrupt-recovery-card">
          <div className="flex items-center justify-between">
            <SectionTitle icon={Hand} tone="text-red-600">Interrupt recovery</SectionTitle>
            <span className={`text-2xl font-extrabold font-display ${analysis.interrupt_recovery >= 80 ? "text-emerald-600" : analysis.interrupt_recovery >= 60 ? "text-amber-600" : "text-red-600"}`} data-testid="interrupt-recovery-score">{analysis.interrupt_recovery}</span>
          </div>
          <p className="text-xs text-slate-500 mt-1">Cut off {analysis.interrupt_count || 1} time{(analysis.interrupt_count || 1) > 1 ? "s" : ""} — how well you regained control.</p>
          <p className="text-slate-600 mt-2 leading-relaxed text-sm">{analysis.interrupt_recovery_note}</p>
        </Section>
      )}

      {assessment?.certificate && (
        <motion.div initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }} className="rounded-3xl border border-amber-300 bg-gradient-to-br from-amber-50 via-white to-emerald-50 p-5 flex flex-wrap items-center gap-4 shadow-[0_10px_30px_-12px_rgba(245,158,11,0.35)]" data-testid="assessment-passed">
          <div className="w-12 h-12 rounded-2xl bg-amber-500 text-black flex items-center justify-center shrink-0"><Award className="w-6 h-6" /></div>
          <div className="flex-1 min-w-[200px]">
            <div className="font-extrabold text-slate-900 text-base md:text-lg font-display">Assessment passed — certificate earned</div>
            <div className="text-sm text-slate-600">{assessment.course_title}: you scored {analysis.vulnerability_handling?.score}/100 on vulnerability handling and sold responsibly.</div>
          </div>
          <Button onClick={() => navigate("/app/courses")} className="rounded-full bg-amber-500 hover:bg-amber-600 text-black font-semibold h-10 px-5" data-testid="view-certificate-btn"><Award className="w-4 h-4 mr-1.5" /> View certificate</Button>
        </motion.div>
      )}

      {analysis.vulnerability_handling && <VulnerabilityReport vh={analysis.vulnerability_handling} sessionId={sessionId} personaName={persona?.name} />}

      {(analysis.vulnerabilities || []).length > 0 && (
        <Section className="p-5 border-violet-200 bg-gradient-to-br from-violet-50/60 via-white to-white" testid="wildcard-reveal-card">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <SectionTitle icon={Shuffle} tone="text-violet-600">The Wildcard reveal</SectionTitle>
            <span className="text-xs font-semibold text-violet-700 bg-violet-100 border border-violet-200 rounded-full px-2.5 py-1" data-testid="wildcard-found-count">
              {analysis.vulnerabilities.filter((v) => v.uncovered).length} of {analysis.vulnerabilities.length} soft spots found
            </span>
          </div>
          <p className="text-xs text-slate-500 mt-1">{persona?.name || "The customer"} was hiding these. Green = you uncovered it and used it. Red = it was there for the taking and you walked past.</p>
          <ul className="mt-3 grid sm:grid-cols-2 gap-2">
            {analysis.vulnerabilities.map((v) => (
              <li key={v.id} className={`rounded-xl border px-3 py-2.5 text-sm ${v.uncovered ? "bg-emerald-50/70 border-emerald-200/70" : "bg-red-50/60 border-red-200/70"}`} data-testid={`vuln-${v.id}`}>
                <div className="flex items-center gap-2 font-semibold text-slate-900">
                  {v.uncovered ? <Eye className="w-4 h-4 text-emerald-600 shrink-0" /> : <EyeOff className="w-4 h-4 text-red-500 shrink-0" />}
                  {v.label}
                  <span className={`ml-auto text-[10px] font-mono uppercase tracking-wider ${v.uncovered ? "text-emerald-700" : "text-red-600"}`}>{v.uncovered ? "Uncovered" : "Missed"}</span>
                </div>
                <p className="text-slate-600 text-xs mt-1 leading-relaxed">{v.note}</p>
              </li>
            ))}
          </ul>
        </Section>
      )}

      <div className="grid md:grid-cols-2 gap-6">
        <Section className="p-5" testid="strengths-card">
          <SectionTitle icon={Trophy} tone="text-emerald-600">What worked</SectionTitle>
          <ul className="mt-3 space-y-2">
            {(analysis.strengths || []).map((t, i) => <li key={`${i}-${t.slice(0, 24)}`} className="text-sm text-slate-700 flex gap-2.5 rounded-xl bg-emerald-50/70 border border-emerald-200/60 px-3 py-2"><span className="w-5 h-5 rounded-full bg-emerald-500 text-white text-[10px] flex items-center justify-center shrink-0 mt-0.5">✓</span>{t}</li>)}
          </ul>
        </Section>
        <Section className="p-5" testid="missed-card">
          <SectionTitle icon={Lightbulb} tone="text-amber-500">Missed opportunities</SectionTitle>
          <ul className="mt-3 space-y-2">
            {(analysis.missed_opportunities || []).map((t, i) => <li key={`${i}-${t.slice(0, 24)}`} className="text-sm text-slate-700 flex gap-2.5 rounded-xl bg-amber-50/70 border border-amber-200/60 px-3 py-2"><span className="w-5 h-5 rounded-full bg-amber-500 text-white text-[10px] flex items-center justify-center shrink-0 mt-0.5">→</span>{t}</li>)}
          </ul>
        </Section>
      </div>

      {(analysis.alternative_responses || []).length > 0 && (
        <Section className="p-5" testid="rewrites-card">
          <SectionTitle icon={RefreshCw} tone="text-cyan-600">Try this instead</SectionTitle>
          <div className="mt-3 grid md:grid-cols-2 gap-3">
            {analysis.alternative_responses.map((a, i) => (
              <div key={`${i}-${(a.instead_of || "").slice(0, 24)}`} className="text-sm rounded-2xl border border-slate-200 overflow-hidden" data-testid={`rewrite-${i}`}>
                <div className="px-3.5 py-2.5 bg-slate-50 text-slate-500 flex gap-2"><span className="text-[10px] font-mono uppercase tracking-wider text-slate-400 mt-0.5 shrink-0">You said</span><span className="line-through">{a.instead_of}</span></div>
                <div className="px-3.5 py-3 bg-emerald-50 border-t border-emerald-200 flex gap-2"><span className="text-[10px] font-mono uppercase tracking-wider text-emerald-700 mt-0.5 shrink-0">Try this</span><span className="text-emerald-900 font-medium leading-relaxed">{a.try}</span></div>
              </div>
            ))}
          </div>
        </Section>
      )}

      <Section className="p-5" testid="action-plan-card">
        <SectionTitle icon={Route} tone="text-emerald-600">Your action plan</SectionTitle>
        <ol className="mt-3 grid md:grid-cols-3 gap-3">
          {(analysis.action_plan || []).map((t, i) => (
            <li key={`${i}-${t.slice(0, 24)}`} className="text-sm text-slate-700 rounded-2xl bg-slate-50 border border-slate-200/80 p-4 flex gap-3"><span className="w-7 h-7 rounded-xl bg-slate-900 text-white text-xs font-bold flex items-center justify-center shrink-0">{i + 1}</span><span className="leading-relaxed">{t}</span></li>
          ))}
        </ol>
      </Section>

      <ShareCard sessionId={sessionId} persona={persona} analysis={analysis} />
    </motion.div>
  );
}

function ShareCard({ sessionId, persona, analysis }) {
  const [link, setLink] = useState(null);
  const [busy, setBusy] = useState(false);

  const share = async () => {
    if (!sessionId) return;
    setBusy(true);
    try {
      const url = link || `${window.location.origin}${(await api.post(`/roleplay/${sessionId}/share`)).data.path}`;
      setLink(url);
      const text = `I just scored ${analysis.overall_rating ?? ""} against ${persona.name} (${persona.title}) on Pitch Craft${analysis.deal_won ? " — and closed the deal" : ""}.`;
      if (navigator.share) {
        try { await navigator.share({ title: "My Pitch Craft roleplay", text, url }); return; } catch (e) { if (e?.name === "AbortError") return; }
      }
      await navigator.clipboard.writeText(url);
      toast.success("Share link copied to your clipboard");
    } catch { toast.error("Couldn't create a share link"); }
    finally { setBusy(false); }
  };

  const copy = async () => { await navigator.clipboard.writeText(link); toast.success("Link copied"); };

  const shareImage = async () => {
    if (!sessionId) return;
    setBusy(true);
    try {
      const path = link ? new URL(link).pathname : (await api.post(`/roleplay/${sessionId}/share`)).data.path;
      const url = `${window.location.origin}${path}`;
      setLink(url);
      const shareId = path.split("/").pop();
      const res = await api.get(`/share/${shareId}/card.png`, { responseType: "blob" });
      const file = new File([res.data], `pitchcraft-scorecard-${shareId}.png`, { type: "image/png" });
      if (navigator.canShare?.({ files: [file] })) {
        try { await navigator.share({ files: [file], title: "My Pitch Craft scorecard", text: `Scored ${analysis.overall_rating ?? ""} vs ${persona.name} on Pitch Craft — ${url}` }); return; }
        catch (e) { if (e?.name === "AbortError") return; }
      }
      const a = Object.assign(document.createElement("a"), { href: URL.createObjectURL(file), download: file.name });
      document.body.appendChild(a); a.click(); a.remove();
      toast.success("Scorecard image downloaded — ready to post");
    } catch { toast.error("Couldn't build the scorecard image"); }
    finally { setBusy(false); }
  };

  return (
    <div className={`${EPIC} p-5 border-emerald-200/80`} data-testid="share-card">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h3 className="font-semibold text-slate-900 flex items-center gap-2"><Share2 className="w-4 h-4 text-emerald-600" /> Share this conversation</h3>
          <p className="text-sm text-slate-500 mt-1">Send your scorecard and transcript to your manager or team — anyone with the link can view it.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button onClick={shareImage} disabled={busy || !sessionId} variant="outline" className="h-11 px-4 border-slate-300 bg-white text-slate-900 hover:bg-emerald-50 hover:border-emerald-300 font-semibold" data-testid="share-image-btn">
            <ImageDown className="w-4 h-4 mr-2" /> Share image
          </Button>
          <Button onClick={share} disabled={busy || !sessionId} className="bg-emerald-500 hover:bg-emerald-600 text-black font-semibold h-11 px-5" data-testid="share-conversation-btn">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Share2 className="w-4 h-4 mr-2" /> Share conversation</>}
          </Button>
        </div>
      </div>
      {link && (
        <div className="mt-4 space-y-3" data-testid="share-link-row">
          <div className="flex items-center gap-2">
            <Input readOnly value={link} className="flex-1 bg-slate-50 border-slate-200 text-slate-700 text-xs font-mono h-10" data-testid="share-link-input" onFocus={(e) => e.target.select()} />
            <Button onClick={copy} variant="outline" className="h-10 border-slate-300 bg-white text-slate-900" data-testid="share-copy-btn"><Copy className="w-4 h-4 mr-1.5" /> Copy</Button>
            <a href={link} target="_blank" rel="noreferrer" className="h-10 inline-flex items-center px-3 rounded-md border border-slate-300 bg-white text-sm text-slate-900 hover:bg-slate-50" data-testid="share-open-link"><ExternalLink className="w-4 h-4" /></a>
          </div>
          <img src={`${process.env.REACT_APP_BACKEND_URL}/api/share/${link.split("/").pop()}/card.png`} alt="Scorecard preview" className="w-full max-w-lg rounded-xl border border-slate-200 shadow-sm" data-testid="share-card-preview" />
        </div>
      )}
    </div>
  );
}
